'use client';

import type { ComponentType, HTMLAttributes} from 'react';
import { useMemo } from 'react';

import Star from '@uikit/icons/Star';

import { AdaptiveGallery } from '@ui/uikit/components/common/AdaptiveGallery';
import { Amount } from '@ui/uikit/components/common/Amount';
import { InfoCard } from '@ui/uikit/components/common/InfoCard';
import { Rating } from '@ui/uikit/components/icons/mono/Rating';
import { ThumbsUp } from '@ui/uikit/components/icons/mono/ThumbsUp';


type StatKey = 'userScore' | 'validationScore' | 'generalRating';

interface StatConfigItem {
  key: StatKey;
  label: string;
  color: string;
  icon: ComponentType<HTMLAttributes<SVGElement>>;
  value?: number;
  tooltip?: string;
}

interface DynamicStatConfigItem extends Omit<StatConfigItem, 'key'> {
  key: string;
}

const hasValue = (item: { value?: number }) =>
  item.value !== null && item.value !== undefined && item.value > 0;

const statsConfig: StatConfigItem[] = [
  {
    key: 'userScore',
    label: 'User Score',
    color: 'var(--text-color-orange)',
    icon: Star,
    tooltip:
      'Reflects users&apos; overall satisfaction and experience based on their reviews and ratings, rated from 1 to 5',
  },
  {
    key: 'validationScore',
    label: 'Validation Score',
    color: 'var(--text-color-green)',
    icon: ThumbsUp,
    tooltip:
      'Represents the app&apos;s performance and quality rated by the community during validation testing, on a scale from 3 to 5',
  },
  {
    key: 'generalRating',
    label: 'General Rating',
    color: 'var(--text-color-quaternary)',
    icon: Rating,
    tooltip:
      'Indicates the app&apos;s overall rank in the Magic Store, comparing it to all other apps across categories',
  },
];

const getStats = (props: AppStatsCardsProps): (StatConfigItem | DynamicStatConfigItem)[] => {
  const baseStats: StatConfigItem[] = statsConfig.map((item) => ({
    ...item,
    value: props[item.key],
  }));

  const chainStat: DynamicStatConfigItem = {
    key: props.chainName ?? 'networkName',
    label: props.chainName ?? 'Network',
    value: props.chainRank ?? 0,
    color: 'var(--text-color-quaternary)',
    icon: Rating,
    tooltip:
      'Indicates the app’s overall rank in the Magic Store within the specific network, comparing it to all other apps on that network',
  };

  const categoryStat: DynamicStatConfigItem = {
    key: props.categoryName ?? 'categoryName',
    label: props.categoryName ? `In ${props.categoryName}` : 'In category',
    value: props.categoryRank ?? 0,
    color: 'var(--text-color-quaternary)',
    icon: Rating,
    tooltip:
      'Indicates the app’s rank in the Magic Store within the specific category, compared to all other apps there',
  };

  return [...baseStats, categoryStat, chainStat].sort((a, b) => {
    if (hasValue(a) === hasValue(b)) {
      return 0;
    }

    return hasValue(a) ? -1 : 1;
  });
};

interface AppStatsCardsProps {
  chainRank?: number;
  chainName?: string;
  generalRating?: number;
  categoryRank?: number;
  categoryName?: string;
  userScore?: number;
  validationScore?: number;
  breakout?: boolean;
}

export const AppStatsCards = ({
  chainRank,
  chainName,
  generalRating,
  categoryRank,
  categoryName,
  userScore,
  validationScore,
  breakout,
}: AppStatsCardsProps) => {
  const stats = useMemo(
    () =>
      getStats({
        chainRank,
        chainName,
        generalRating,
        categoryRank,
        categoryName,
        userScore,
        validationScore,
      }),
    [categoryName, categoryRank, chainName, chainRank, generalRating, userScore, validationScore],
  );

  const hasAtLeastOneStat = useMemo(() => stats.some((stat) => hasValue(stat)), [stats]);

  if (!hasAtLeastOneStat) {
    return null;
  }

  return (
    <AdaptiveGallery
      numberOfSlides={2}
      spacing="8px"
      centerSlides={false}
      breakout={breakout}
      breakpoints={{
        SMALL: {
          numberOfSlides: 2,
          contentPadding: 16,
        },
        MEDIUM: {
          numberOfSlides: 5,
          contentPadding: 16,
        },
        LARGE: {
          numberOfSlides: 5,
          contentPadding: 16,
          spacing: '16px',
        },
        XLARGE: {
          numberOfSlides: 5,
          spacing: '16px',
        },
      }}
    >
      {stats.map((statItem) => {
        const value = statItem.value ? Math.round(statItem.value * 10) / 10 : '—';

        return (
          <AdaptiveGallery.Slide key={statItem.key}>
            <InfoCard
              caption={statItem.label}
              tooltip={statItem.tooltip}
              showPlaceholder={!hasValue(statItem)}
              height={84}
            >
              <Amount value={String(value)} color={statItem.color} icon={statItem.icon} />
            </InfoCard>
          </AdaptiveGallery.Slide>
        );
      })}
    </AdaptiveGallery>
  );
};
